import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import type { CSSProperties } from 'react';
import type { EChartsOption } from 'echarts';
import type { EChartsType } from 'echarts/core';

export interface StaticEChartHandle {
  getEchartsInstance: () => EChartsType | null;
}

interface StaticEChartProps {
  echarts: {
    init: (container: HTMLElement) => EChartsType;
  };
  option: EChartsOption;
  style?: CSSProperties;
  className?: string;
}

const StaticEChart = forwardRef<StaticEChartHandle, StaticEChartProps>(
  ({ echarts, option, style, className = '' }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const instanceRef = useRef<EChartsType | null>(null);

    useImperativeHandle(ref, () => ({
      getEchartsInstance: () => {
        const instance = instanceRef.current;
        if (!instance || instance.isDisposed?.()) return null;
        return instance;
      },
    }));

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const instance = echarts.init(container);
      instanceRef.current = instance;
      instance.setOption(option);

      return () => {
        if (!instance.isDisposed?.()) {
          instance.dispose();
        }
        instanceRef.current = null;
      };
    }, [echarts]);

    useEffect(() => {
      const instance = instanceRef.current;
      if (!instance || instance.isDisposed?.()) return;
      instance.setOption(option);
    }, [option]);

    return (
      <div
        ref={containerRef}
        style={style}
        className={`echarts-for-react ${className}`.trim()}
      />
    );
  },
);

StaticEChart.displayName = 'StaticEChart';

export default StaticEChart;
